import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dice1, Dice2, Dice3, Dice4, Dice5, Dice6, Lock, MapPin, Flame, Trophy, TrendingUp, TrendingDown, RotateCcw, Zap } from 'lucide-react';
import { getGamblingImage } from '../data/gamblingImages';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const DICE_ICONS = [null, Dice1, Dice2, Dice3, Dice4, Dice5, Dice6];

// Shared sub-components (must be outside render to avoid reconciliation issues)
const DieIcon = ({ value }) => {
  const Icon = DICE_ICONS[value] || Dice1;
  return <Icon size={48} className="text-[#333]" />;
};

const BJCard = ({ card, delay = 0, faceDown = false }) => (
  <motion.div
    initial={{ rotateY: 180, opacity: 0, y: -30 }}
    animate={{ rotateY: 0, opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.4, type: 'spring' }}
    className={`w-14 h-20 rounded-lg flex items-center justify-center shadow-lg ${faceDown ? 'bg-gradient-to-br from-[#8B1A1A] to-[#4A0000] border border-[#C9A84C]/30' : 'bg-gradient-to-b from-[#1a1f28] to-[#0a0e12] border border-[#C9A84C]/20'}`}>
    {faceDown ? (
      <span className="font-['Cormorant_Garamond'] text-lg text-[#C9A84C]/30">K</span>
    ) : (
      <span className="font-['Outfit'] text-base font-bold" style={{ color: card?.includes('♥') || card?.includes('♦') ? '#EF4444' : '#E8E0D0' }}>
        {card}
      </span>
    )}
  </motion.div>
);

// ─── CARD PACK — Green felt, gold foil card backs, fan animation ───
const CardPackGame = ({ gameId, venue, onClose, onResult }) => {
  const [bet, setBet] = useState(500);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [spinIndex, setSpinIndex] = useState(0);
  const [spinCards, setSpinCards] = useState([]);
  const [fanRevealed, setFanRevealed] = useState(false);

  const CARDS = [
    { id: 'bust', label: 'BUST', color: '#8B1A1A', bg: '#1a0505' },
    { id: 'break_even', label: 'PUSH', color: '#6B7280', bg: '#111820' },
    { id: 'win_2x', label: '2X', color: '#4A7AB5', bg: '#0a1520' },
    { id: 'win_5x', label: '5X', color: '#C9A84C', bg: '#1a1505' },
    { id: 'win_10x', label: '10X', color: '#FFD700', bg: '#1a1a05' },
    { id: 'jackpot', label: 'JACKPOT', color: '#FF4500', bg: '#1a0a00' },
  ];

  const play = async () => {
    setSpinning(true); setResult(null); setSpinIndex(0); setFanRevealed(false);
    try {
      const res = await fetch(`${API}/api/gambling/${gameId}/card-pack`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue_id: venue.id, bet_amount: bet }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Error'); setSpinning(false); return; }
      const seq = data.spin_sequence || [];
      setSpinCards(seq);
      for (let i = 0; i < seq.length; i++) {
        await new Promise(r => setTimeout(r, 150 + i * 50));
        setSpinIndex(i + 1);
      }
      await new Promise(r => setTimeout(r, 400));
      setFanRevealed(true);
      await new Promise(r => setTimeout(r, 300));
      setResult(data); setSpinning(false); onResult(data);
    } catch { setSpinning(false); }
  };

  const currentCard = spinning && spinIndex > 0 && spinCards.length > 0
    ? CARDS.find(c => c.id === spinCards[Math.min(spinIndex - 1, spinCards.length - 1)]) || CARDS[0]
    : result ? CARDS.find(c => c.id === result.result_card) || CARDS[0] : null;

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#C9A84C]">POLICY CARDS</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest">{venue.name} — {venue.type === 'backroom' ? 'BACK ROOM (2x)' : venue.type === 'waterfront' ? 'WATERFRONT' : 'STREET'}</p>
      </div>

      {/* Green felt table surface */}
      <div className="relative rounded-2xl overflow-hidden min-h-[280px] flex items-center justify-center"
        style={{ background: 'linear-gradient(180deg, #0a2010 0%, #071a0c 50%, #051408 100%)' }}>
        {/* Felt grain texture */}
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle, #2D5A27 0.5px, transparent 0.5px)', backgroundSize: '8px 8px' }} />
        {/* Table edge */}
        <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-b from-[#8B6914]/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-t from-[#8B6914]/30 to-transparent" />

        {/* Card fan display */}
        <div className="relative z-10 flex justify-center py-8">
          {!spinning && !result && (
            <div className="flex justify-center" style={{ perspective: '600px' }}>
              {[...Array(5)].map((_, i) => (
                <motion.div key={i}
                  className="w-24 h-36 rounded-lg border-2 border-[#C9A84C]/40 flex items-center justify-center shadow-xl"
                  style={{
                    background: 'linear-gradient(135deg, #1a1505 0%, #0f0d05 50%, #1a1505 100%)',
                    marginLeft: i === 0 ? 0 : '-40px',
                    transformOrigin: 'bottom center',
                    zIndex: i,
                  }}
                  initial={{ rotate: -12 + i * 6 }}
                  animate={{ rotate: -12 + i * 6, y: [0, -3, 0] }}
                  transition={{ y: { duration: 2, repeat: Infinity, delay: i * 0.2 } }}
                >
                  {/* Gold foil pattern on card back */}
                  <div className="w-16 h-24 rounded border border-[#C9A84C]/20 flex items-center justify-center"
                    style={{ background: 'repeating-linear-gradient(45deg, #C9A84C08, #C9A84C08 2px, transparent 2px, transparent 8px)' }}>
                    <span className="font-['Cormorant_Garamond'] text-2xl text-[#C9A84C]/30 font-bold">K</span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {(spinning || result) && currentCard && (
            <motion.div
              className="w-36 h-52 rounded-xl border-2 flex items-center justify-center relative overflow-hidden shadow-2xl"
              style={{ borderColor: currentCard.color, background: currentCard.bg }}
              animate={spinning ? { rotateY: [0, 180, 360], scale: [1, 1.05, 1] } : { scale: [1.05, 1] }}
              transition={spinning ? { duration: 0.3, repeat: Infinity } : { duration: 0.3 }}
            >
              <div className="text-center">
                <p className="font-['Cormorant_Garamond'] text-4xl font-bold" style={{ color: currentCard.color }}>{currentCard.label}</p>
                {result && (
                  <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className="font-['Outfit'] text-xs mt-2"
                    style={{ color: result.net >= 0 ? '#4ADE80' : '#EF4444' }}>
                    {result.net >= 0 ? `+$${result.net.toLocaleString()}` : `-$${Math.abs(result.net).toLocaleString()}`}
                  </motion.p>
                )}
              </div>
              {(spinning || (result && result.multiplier > 1)) && (
                <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#C9A84C]/10 to-transparent"
                  animate={{ x: ['-100%', '200%'] }} transition={{ duration: 1, repeat: Infinity }} />
              )}
            </motion.div>
          )}
        </div>
      </div>

      {/* Bet controls */}
      {!spinning && !result && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 justify-center">
            {[500, 1000, 5000, 25000, 100000].map(v => (
              <button key={v} onClick={() => setBet(v)}
                className={`px-3 py-1.5 rounded font-['Outfit'] text-[10px] tracking-widest transition-all ${
                  bet === v ? 'bg-[#C9A84C]/20 border border-[#C9A84C]/50 text-[#C9A84C]' : 'bg-[#111820] border border-[#1A2535] text-[#E8E0D0]/30 hover:text-[#E8E0D0]/60'
                }`} data-testid={`bet-${v}`}>
                ${v >= 1000 ? `${v/1000}K` : v}
              </button>
            ))}
          </div>
          <button onClick={play}
            className="w-full py-3 bg-gradient-to-r from-[#C9A84C] to-[#8B6914] text-[#050505] font-['Outfit'] text-sm tracking-widest rounded-lg hover:brightness-110 transition-all shadow-lg shadow-[#C9A84C]/15"
            data-testid="play-card-pack-btn">
            DRAW CARD — ${bet.toLocaleString()}
          </button>
        </div>
      )}

      {/* Result */}
      <AnimatePresence>
        {result && !spinning && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className={`p-4 rounded-lg border text-center ${result.net > 0 ? 'bg-[#0a1a0a] border-[#4ADE80]/20' : result.net === 0 ? 'bg-[#111820] border-[#1A2535]' : 'bg-[#1a0505] border-[#8B1A1A]/20'}`}>
            <p className="font-['Outfit'] text-xs tracking-widest mb-1" style={{ color: result.net > 0 ? '#4ADE80' : result.net === 0 ? '#6B7280' : '#EF4444' }}>
              {result.net > 0 ? 'WIN' : result.net === 0 ? 'PUSH' : 'BUST'}
            </p>
            <p className="font-['Cormorant_Garamond'] text-2xl font-bold" style={{ color: result.net >= 0 ? '#C9A84C' : '#8B1A1A' }}>
              {result.net >= 0 ? `+$${result.net.toLocaleString()}` : `-$${Math.abs(result.net).toLocaleString()}`}
            </p>
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-1">Balance: ${result.cash_after.toLocaleString()}</p>
            <button onClick={() => setResult(null)}
              className="mt-3 px-4 py-1.5 bg-[#111820] border border-[#1A2535] rounded font-['Outfit'] text-[10px] text-[#E8E0D0]/40 hover:text-[#C9A84C] tracking-widest transition-colors"
              data-testid="play-again-btn">
              <RotateCcw size={12} className="inline mr-1" /> AGAIN
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


// ─── STREET DICE — Concrete texture, graffiti vibes, bouncing dice ───
const StreetDiceGame = ({ gameId, venue, onClose, onResult }) => {
  const [bet, setBet] = useState(1000);
  const [sideBet, setSideBet] = useState(null);
  const [sideBetAmt, setSideBetAmt] = useState(500);
  const [rolling, setRolling] = useState(false);
  const [result, setResult] = useState(null);
  const [animRoll, setAnimRoll] = useState(null);
  const [rollIndex, setRollIndex] = useState(-1);

  const SIDE_BETS = [
    { id: 'hard_way', label: 'HARD WAY', desc: 'Doubles hit your point', payout: '8x' },
    { id: 'any_craps', label: 'ANY CRAPS', desc: '2, 3, or 12', payout: '7x' },
    { id: 'snake_eyes', label: 'SNAKE EYES', desc: 'Double 1s', payout: '30x' },
  ];

  const play = async () => {
    setRolling(true); setResult(null); setRollIndex(-1); setAnimRoll(null);
    try {
      const body = { venue_id: venue.id, bet_amount: bet };
      if (sideBet) { body.side_bet_id = sideBet; body.side_bet_amount = sideBetAmt; }
      const res = await fetch(`${API}/api/gambling/${gameId}/street-dice`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Error'); setRolling(false); return; }
      for (let i = 0; i < data.rolls.length; i++) {
        setAnimRoll({ die1: Math.ceil(Math.random() * 6), die2: Math.ceil(Math.random() * 6) });
        await new Promise(r => setTimeout(r, 250));
        setAnimRoll({ die1: Math.ceil(Math.random() * 6), die2: Math.ceil(Math.random() * 6) });
        await new Promise(r => setTimeout(r, 250));
        setAnimRoll(data.rolls[i]); setRollIndex(i);
        await new Promise(r => setTimeout(r, 700));
      }
      await new Promise(r => setTimeout(r, 400));
      setResult(data); setRolling(false); onResult(data);
    } catch { setRolling(false); }
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#4A7AB5]">STREET DICE</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest">{venue.name} — {venue.type === 'backroom' ? 'BACK ROOM (2x)' : 'STREET'}</p>
      </div>

      {/* Concrete/asphalt surface */}
      <div className="relative rounded-2xl overflow-hidden py-8"
        style={{ background: 'linear-gradient(180deg, #1a1c1e 0%, #141618 50%, #101214 100%)' }}>
        {/* Asphalt grain */}
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: 'radial-gradient(circle, #888 0.3px, transparent 0.3px), radial-gradient(circle, #666 0.5px, transparent 0.5px)',
          backgroundSize: '7px 7px, 13px 13px',
          backgroundPosition: '0 0, 4px 4px',
        }} />
        {/* Graffiti-style chalk lines */}
        <div className="absolute bottom-2 left-4 font-['Outfit'] text-[8px] text-[#4A7AB5]/15 tracking-[0.5em] rotate-[-2deg]">SHOOTERS ONLY</div>
        <div className="absolute top-3 right-4 font-['Outfit'] text-[8px] text-[#C9A84C]/10 tracking-[0.3em] rotate-[1deg]">NO COPS</div>

        {/* Dice */}
        <div className="relative z-10 flex justify-center gap-8">
          <motion.div
            animate={rolling && !animRoll ? { rotate: [0, 90, 180, 270, 360], y: [0, -30, 5, -10, 0] } : {}}
            transition={{ duration: 0.3, repeat: rolling && !animRoll ? Infinity : 0 }}
            className="w-20 h-20 bg-[#E8E0D0] rounded-xl flex items-center justify-center shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
            style={{ border: '2px solid #d4c9b5' }}>
            {animRoll ? <DieIcon value={animRoll.die1} /> : <Dice1 size={48} className="text-[#333]/20" />}
          </motion.div>
          <motion.div
            animate={rolling && !animRoll ? { rotate: [360, 270, 180, 90, 0], y: [0, -25, 8, -15, 0] } : {}}
            transition={{ duration: 0.35, repeat: rolling && !animRoll ? Infinity : 0 }}
            className="w-20 h-20 bg-[#E8E0D0] rounded-xl flex items-center justify-center shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
            style={{ border: '2px solid #d4c9b5' }}>
            {animRoll ? <DieIcon value={animRoll.die2} /> : <Dice1 size={48} className="text-[#333]/20" />}
          </motion.div>
        </div>
      </div>

      {/* Roll total */}
      {animRoll && (
        <motion.p key={`${animRoll.die1}-${animRoll.die2}`}
          initial={{ scale: 1.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="text-center font-['Cormorant_Garamond'] text-3xl font-bold text-[#C9A84C]">
          {animRoll.total}
          {result && rollIndex === 0 && result.point && <span className="text-sm text-[#E8E0D0]/30 ml-2">POINT: {result.point}</span>}
        </motion.p>
      )}

      {/* Roll history */}
      {result && result.rolls.length > 1 && (
        <div className="flex gap-1 justify-center flex-wrap">
          {result.rolls.map((r, i) => (
            <span key={i} className={`font-['Outfit'] text-[10px] px-2 py-0.5 rounded ${
              i === 0 ? 'bg-[#C9A84C]/10 text-[#C9A84C]' :
              i === result.rolls.length - 1 ? (result.result === 'win' ? 'bg-[#4ADE80]/10 text-[#4ADE80]' : 'bg-[#EF4444]/10 text-[#EF4444]') :
              'bg-[#111820] text-[#E8E0D0]/30'
            }`}>{r.total}</span>
          ))}
        </div>
      )}

      {/* Bet controls */}
      {!rolling && !result && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 justify-center">
            {[1000, 5000, 10000, 50000, 250000].map(v => (
              <button key={v} onClick={() => setBet(v)}
                className={`px-3 py-1.5 rounded font-['Outfit'] text-[10px] tracking-widest transition-all ${
                  bet === v ? 'bg-[#4A7AB5]/20 border border-[#4A7AB5]/50 text-[#4A7AB5]' : 'bg-[#111820] border border-[#1A2535] text-[#E8E0D0]/30'
                }`}>${v >= 1000 ? `${v/1000}K` : v}</button>
            ))}
          </div>
          <div className="bg-[#0a0e12] rounded-lg p-3">
            <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest mb-2">SIDE BET (OPTIONAL)</p>
            <div className="flex gap-2">
              {SIDE_BETS.map(sb => (
                <button key={sb.id} onClick={() => setSideBet(sideBet === sb.id ? null : sb.id)}
                  className={`flex-1 p-2 rounded text-center transition-all ${sideBet === sb.id ? 'bg-[#C9A84C]/10 border border-[#C9A84C]/30' : 'bg-[#111820] border border-[#1A2535]'}`}>
                  <p className="font-['Outfit'] text-[9px] tracking-widest" style={{ color: sideBet === sb.id ? '#C9A84C' : '#E8E0D0' }}>{sb.label}</p>
                  <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20">{sb.payout}</p>
                </button>
              ))}
            </div>
          </div>
          <button onClick={play}
            className="w-full py-3 bg-[#4A7AB5] text-[#050505] font-['Outfit'] text-sm tracking-widest rounded-lg hover:brightness-110 transition-all"
            data-testid="play-dice-btn">
            ROLL — ${bet.toLocaleString()}{sideBet ? ` + $${sideBetAmt.toLocaleString()} SIDE` : ''}
          </button>
        </div>
      )}

      {/* Result */}
      <AnimatePresence>
        {result && !rolling && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className={`p-4 rounded-lg border text-center ${result.net > 0 ? 'bg-[#0a1a0a] border-[#4ADE80]/20' : 'bg-[#1a0505] border-[#8B1A1A]/20'}`}>
            <p className="font-['Outfit'] text-xs tracking-widest mb-1" style={{ color: result.net > 0 ? '#4ADE80' : '#EF4444' }}>{result.result.toUpperCase()}</p>
            <p className="font-['Cormorant_Garamond'] text-2xl font-bold" style={{ color: result.net >= 0 ? '#C9A84C' : '#8B1A1A' }}>
              {result.net >= 0 ? `+$${result.net.toLocaleString()}` : `-$${Math.abs(result.net).toLocaleString()}`}
            </p>
            {result.side_bet && (
              <p className="font-['Outfit'] text-[10px] mt-1" style={{ color: result.side_bet.won ? '#FFD700' : '#E8E0D0' }}>
                {result.side_bet.label}: {result.side_bet.won ? `WON $${result.side_bet.payout.toLocaleString()}` : 'MISS'}
              </p>
            )}
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-1">Balance: ${result.cash_after.toLocaleString()}</p>
            <button onClick={() => { setResult(null); setAnimRoll(null); setRollIndex(-1); }}
              className="mt-3 px-4 py-1.5 bg-[#111820] border border-[#1A2535] rounded font-['Outfit'] text-[10px] text-[#E8E0D0]/40 hover:text-[#4A7AB5] tracking-widest transition-colors"
              data-testid="roll-again-btn">
              <RotateCcw size={12} className="inline mr-1" /> ROLL AGAIN
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


// ─── SLOTS — Neon glow machine, lever, spinning reel columns ───
const SlotsGame = ({ gameId, venue, onClose, onResult }) => {
  const [bet, setBet] = useState(1000);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [leverPulled, setLeverPulled] = useState(false);

  const play = async () => {
    setSpinning(true); setResult(null); setLeverPulled(true);
    await new Promise(r => setTimeout(r, 300));
    try {
      const res = await fetch(`${API}/api/gambling/${gameId}/slots`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue_id: venue.id, bet_amount: bet }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail); setSpinning(false); setLeverPulled(false); return; }
      await new Promise(r => setTimeout(r, 2000));
      setResult(data); setSpinning(false); setLeverPulled(false); onResult(data);
    } catch { setSpinning(false); setLeverPulled(false); }
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#F59E0B]">SLOT MACHINE</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest">{venue.name}</p>
      </div>

      {/* Neon machine body */}
      <div className="relative rounded-2xl overflow-hidden border-2 py-6"
        style={{
          background: 'linear-gradient(180deg, #1a0f05 0%, #0f0a02 50%, #1a0f05 100%)',
          borderColor: '#F59E0B30',
          boxShadow: '0 0 30px rgba(245,158,11,0.08), inset 0 0 30px rgba(245,158,11,0.03)',
        }}>
        {/* Neon glow edges */}
        <div className="absolute inset-x-0 top-0 h-1" style={{ background: 'linear-gradient(90deg, transparent, #F59E0B40, transparent)' }} />
        <div className="absolute inset-x-0 bottom-0 h-1" style={{ background: 'linear-gradient(90deg, transparent, #F59E0B40, transparent)' }} />

        <div className="flex items-center justify-center gap-1 px-4">
          {/* Reels */}
          <div className="flex gap-2">
            {[0, 1, 2].map(i => (
              <div key={i} className="relative">
                {/* Reel window */}
                <div className="w-20 h-28 rounded-lg overflow-hidden border"
                  style={{ borderColor: '#F59E0B25', background: '#050505' }}>
                  {/* Reel column animation */}
                  <motion.div className="flex flex-col items-center justify-center h-full"
                    animate={spinning ? { y: [0, -100, 100, -50, 50, 0] } : {}}
                    transition={spinning ? { duration: 0.2, repeat: Infinity, delay: i * 0.06 } : {}}>
                    {result ? (
                      <motion.span initial={{ scale: 2, opacity: 0, rotateX: 90 }}
                        animate={{ scale: 1, opacity: 1, rotateX: 0 }}
                        transition={{ delay: i * 0.4, type: 'spring', stiffness: 200 }}
                        className="text-3xl font-bold" style={{ color: result.reels[i]?.color }}>
                        {result.reels[i]?.label}
                      </motion.span>
                    ) : <span className="text-2xl text-[#F59E0B]/10">?</span>}
                  </motion.div>
                </div>
                {/* Neon underglow per reel */}
                {result && result.reels[i] && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.4 }}
                    className="absolute -bottom-1 inset-x-1 h-1 rounded-full blur-sm"
                    style={{ background: result.reels[i]?.color }} />
                )}
              </div>
            ))}
          </div>

          {/* Lever */}
          <div className="ml-3 flex flex-col items-center">
            <motion.div
              className="w-4 h-16 rounded-full relative cursor-pointer"
              style={{ background: 'linear-gradient(180deg, #C9A84C, #8B6914)' }}
              animate={leverPulled ? { scaleY: [1, 0.6, 1] } : {}}
              transition={{ duration: 0.5 }}
              onClick={!spinning && !result ? play : undefined}>
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-[#EF4444] border-2 border-[#B91C1C] shadow-lg shadow-[#EF4444]/30" />
            </motion.div>
            <p className="font-['Outfit'] text-[7px] text-[#F59E0B]/30 mt-1">PULL</p>
          </div>
        </div>
      </div>

      {result && !spinning && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg border text-center ${result.net > 0 ? 'bg-[#0a1a0a] border-[#4ADE80]/20' : 'bg-[#1a0505] border-[#8B1A1A]/20'}`}>
          {result.is_triple && <p className="font-['Outfit'] text-[#FFD700] text-xs tracking-widest mb-1 animate-pulse">TRIPLE!</p>}
          <p className="font-['Cormorant_Garamond'] text-2xl font-bold" style={{ color: result.net >= 0 ? '#F59E0B' : '#8B1A1A' }}>
            {result.net >= 0 ? `+$${result.net.toLocaleString()}` : `-$${Math.abs(result.net).toLocaleString()}`}
          </p>
          <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px]">Balance: ${result.cash_after?.toLocaleString()}</p>
          <button onClick={() => setResult(null)} className="mt-3 px-4 py-1.5 bg-[#111820] border border-[#1A2535] rounded font-['Outfit'] text-[10px] text-[#E8E0D0]/40 hover:text-[#F59E0B] tracking-widest transition-colors">
            <RotateCcw size={12} className="inline mr-1" /> PULL AGAIN
          </button>
        </motion.div>
      )}
      {!spinning && !result && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 justify-center">
            {[500, 1000, 5000, 25000, 100000].map(v => (
              <button key={v} onClick={() => setBet(v)} className={`px-3 py-1.5 rounded font-['Outfit'] text-[10px] tracking-widest transition-all ${bet === v ? 'bg-[#F59E0B]/20 border border-[#F59E0B]/50 text-[#F59E0B]' : 'bg-[#111820] border border-[#1A2535] text-[#E8E0D0]/30'}`}>
                ${v >= 1000 ? `${v/1000}K` : v}
              </button>
            ))}
          </div>
          <button onClick={play} className="w-full py-3 bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-[#050505] font-['Outfit'] text-sm tracking-widest rounded-lg hover:brightness-110 transition-all shadow-lg shadow-[#F59E0B]/15" data-testid="play-slots-btn">
            PULL — ${bet.toLocaleString()}
          </button>
        </div>
      )}
    </div>
  );
};


// ─── BLACKJACK — Green felt + card flip (already good, keeping with minor polish) ───
const BlackjackGame = ({ gameId, venue, onClose, onResult }) => {
  const [bet, setBet] = useState(5000);
  const [phase, setPhase] = useState('bet');
  const [hand, setHand] = useState(null);
  const [dealing, setDealing] = useState(false);

  const deal = async () => {
    setDealing(true); setHand(null); setPhase('bet');
    try {
      const res = await fetch(`${API}/api/gambling/${gameId}/blackjack`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue_id: venue.id, bet_amount: bet, action: 'deal' }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail); setDealing(false); return; }
      await new Promise(r => setTimeout(r, 800));
      setHand(data);
      if (data.result && !data.can_hit) { setPhase('result'); onResult(data); }
      else { setPhase('playing'); }
      setDealing(false);
    } catch { setDealing(false); }
  };

  const action = async (act) => {
    setDealing(true);
    try {
      const res = await fetch(`${API}/api/gambling/${gameId}/blackjack`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue_id: venue.id, bet_amount: bet, action: act }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail); setDealing(false); return; }
      await new Promise(r => setTimeout(r, 600));
      setHand(data);
      if (!data.can_hit || act === 'stand') { setPhase('result'); onResult(data); }
      setDealing(false);
    } catch { setDealing(false); }
  };

  const RESULT_TEXT = { win: 'YOU WIN', lose: 'DEALER WINS', push: 'PUSH', bust: 'BUST!', blackjack: 'BLACKJACK!', dealer_blackjack: 'DEALER BJ' };
  const RESULT_COLOR = { win: '#4ADE80', lose: '#EF4444', push: '#6B7280', bust: '#EF4444', blackjack: '#FFD700', dealer_blackjack: '#EF4444' };

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#4ADE80]">BLACKJACK</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest">{venue.name}</p>
      </div>

      {/* Table felt */}
      <div className="bg-gradient-to-b from-[#0a1a0a] to-[#050f05] rounded-2xl border border-[#4ADE80]/10 p-6 min-h-[280px] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #4ADE80 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-b from-[#2D5A27]/30 to-transparent" />

        {hand ? (
          <div className="relative z-10 space-y-6">
            <div className="text-center">
              <p className="font-['Outfit'] text-[9px] tracking-[0.3em] text-[#E8E0D0]/20 mb-3">
                DEALER{phase === 'result' ? ` — ${hand.dealer_total}${hand.dealer_bust ? ' BUST' : ''}` : ` showing ${hand.dealer_showing || '?'}`}
              </p>
              <div className="flex justify-center gap-2">
                {hand.dealer_hand?.map((c, i) => (
                  <BJCard key={i} card={c} delay={phase === 'result' ? 0.8 + i * 0.3 : 0} faceDown={phase === 'playing' && i > 0} />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-[#4ADE80]/10" />
              <span className="font-['Outfit'] text-[8px] tracking-widest text-[#4ADE80]/30">VS</span>
              <div className="flex-1 h-px bg-[#4ADE80]/10" />
            </div>
            <div className="text-center">
              <p className="font-['Outfit'] text-[9px] tracking-[0.3em] text-[#C9A84C]/40 mb-3">
                YOUR HAND — {hand.player_total}{hand.player_bust ? ' BUST!' : ''}
              </p>
              <div className="flex justify-center gap-2">
                {hand.player_hand?.map((c, i) => <BJCard key={i} card={c} delay={i * 0.3} />)}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full min-h-[200px]">
            <p className="font-['Cormorant_Garamond'] text-xl text-[#4ADE80]/15">PLACE YOUR BET</p>
          </div>
        )}
      </div>

      {phase === 'playing' && !dealing && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 gap-3">
          <button onClick={() => action('hit')}
            className="py-4 bg-gradient-to-r from-[#C9A84C] to-[#B8972F] text-[#050505] font-['Outfit'] text-sm tracking-widest rounded-xl hover:brightness-110 transition-all shadow-lg shadow-[#C9A84C]/20"
            data-testid="blackjack-hit-btn">HIT</button>
          <button onClick={() => action('stand')}
            className="py-4 bg-gradient-to-r from-[#4A7AB5] to-[#3A6A9F] text-[#050505] font-['Outfit'] text-sm tracking-widest rounded-xl hover:brightness-110 transition-all shadow-lg shadow-[#4A7AB5]/20"
            data-testid="blackjack-stand-btn">STAND</button>
        </motion.div>
      )}

      {phase === 'result' && hand && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className={`p-5 rounded-xl border text-center ${hand.net > 0 ? 'bg-[#0a1a0a] border-[#4ADE80]/20' : hand.net === 0 ? 'bg-[#111820] border-[#1A2535]' : 'bg-[#1a0505] border-[#8B1A1A]/20'}`}>
          <p className="font-['Outfit'] text-sm tracking-widest mb-1" style={{ color: RESULT_COLOR[hand.result] }}>{RESULT_TEXT[hand.result]}</p>
          {hand.is_blackjack && <p className="font-['Outfit'] text-[#FFD700] text-[10px] mb-1">1.5x PAYOUT</p>}
          <p className="font-['Cormorant_Garamond'] text-3xl font-bold" style={{ color: hand.net > 0 ? '#4ADE80' : hand.net === 0 ? '#6B7280' : '#8B1A1A' }}>
            {hand.net > 0 ? `+$${hand.net.toLocaleString()}` : hand.net === 0 ? '$0' : `-$${Math.abs(hand.net).toLocaleString()}`}
          </p>
          <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-1">Balance: ${hand.cash_after?.toLocaleString()}</p>
          <button onClick={() => { setHand(null); setPhase('bet'); }}
            className="mt-3 px-6 py-2 bg-[#111820] border border-[#4ADE80]/20 rounded-lg font-['Outfit'] text-[10px] text-[#4ADE80]/60 hover:text-[#4ADE80] tracking-widest transition-colors"
            data-testid="blackjack-again-btn">
            <RotateCcw size={12} className="inline mr-1" /> DEAL AGAIN
          </button>
        </motion.div>
      )}

      {phase === 'bet' && !dealing && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 justify-center">
            {[1000, 5000, 25000, 100000, 500000].map(v => (
              <button key={v} onClick={() => setBet(v)}
                className={`px-3 py-1.5 rounded-lg font-['Outfit'] text-[10px] tracking-widest transition-all ${bet === v ? 'bg-[#4ADE80]/20 border border-[#4ADE80]/50 text-[#4ADE80]' : 'bg-[#111820] border border-[#1A2535] text-[#E8E0D0]/30'}`}>
                ${v >= 1000 ? `${v/1000}K` : v}
              </button>
            ))}
          </div>
          <button onClick={deal}
            className="w-full py-3 bg-gradient-to-r from-[#4ADE80] to-[#22C55E] text-[#050505] font-['Outfit'] text-sm tracking-widest rounded-xl hover:brightness-110 transition-all shadow-lg shadow-[#4ADE80]/20"
            data-testid="play-blackjack-btn">
            DEAL — ${bet.toLocaleString()}
          </button>
        </div>
      )}

      {dealing && (
        <div className="text-center py-4">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
            className="w-8 h-8 border-2 border-[#4ADE80]/20 border-t-[#4ADE80] rounded-full mx-auto" />
        </div>
      )}
    </div>
  );
};


// ─── POKER — Smoke-filled room, dark wood table, NPC portrait ───
const PokerGame = ({ gameId, venue, onClose, onResult }) => {
  const [bet, setBet] = useState(5000);
  const [playing, setPlaying] = useState(false);
  const [result, setResult] = useState(null);

  const play = async () => {
    setPlaying(true); setResult(null);
    try {
      const res = await fetch(`${API}/api/gambling/${gameId}/poker`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue_id: venue.id, bet_amount: bet }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail); setPlaying(false); return; }
      await new Promise(r => setTimeout(r, 1500));
      setResult(data); setPlaying(false); onResult(data);
    } catch { setPlaying(false); }
  };

  const HAND_COLORS = { high_card: '#6B7280', pair: '#4A7AB5', two_pair: '#4A7AB5', three_kind: '#C9A84C', straight: '#C9A84C', flush: '#FFD700', full_house: '#FF6B35', four_kind: '#FF4500' };

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#9F7AEA]">BACK ROOM POKER</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest">{venue.name}</p>
      </div>

      {/* Smoke-filled dark wood room */}
      <div className="relative rounded-2xl overflow-hidden min-h-[200px]"
        style={{ background: 'linear-gradient(180deg, #1a150e 0%, #110d08 50%, #0a0805 100%)' }}>
        {/* Wood grain */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'repeating-linear-gradient(90deg, #8B6914 0px, transparent 1px, transparent 15px, #8B6914 16px)',
        }} />
        {/* Smoke haze */}
        <motion.div className="absolute inset-0 opacity-[0.03]"
          style={{ background: 'radial-gradient(ellipse at 50% 30%, #E8E0D0 0%, transparent 60%)' }}
          animate={{ opacity: [0.02, 0.04, 0.02] }}
          transition={{ duration: 4, repeat: Infinity }} />

        <div className="relative z-10 p-6">
          {playing && (
            <div className="text-center py-8">
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}
                className="w-16 h-16 border-2 border-[#9F7AEA]/30 border-t-[#9F7AEA] rounded-full mx-auto" />
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs mt-4 animate-pulse">DEALING CARDS...</p>
            </div>
          )}

          {result && !playing && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              {/* NPC portrait area */}
              <div className="flex items-center justify-center gap-6">
                {/* Player hand */}
                <div className="text-center flex-1">
                  <p className="font-['Outfit'] text-[9px] tracking-widest text-[#C9A84C]/40 mb-2">YOUR HAND</p>
                  <div className="bg-[#050505]/50 rounded-lg p-3 border border-[#C9A84C]/10">
                    <p className="font-['Cormorant_Garamond'] text-lg font-bold" style={{ color: HAND_COLORS[result.player_hand] || '#C9A84C' }}>{result.hand_display}</p>
                  </div>
                </div>

                {/* VS */}
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-[#9F7AEA]/10 border border-[#9F7AEA]/20 flex items-center justify-center">
                    <span className="font-['Outfit'] text-[8px] text-[#9F7AEA]">VS</span>
                  </div>
                </div>

                {/* NPC hand */}
                <div className="text-center flex-1">
                  <p className="font-['Outfit'] text-[9px] tracking-widest text-[#E8E0D0]/20 mb-2">{result.npc_name?.toUpperCase()}</p>
                  <div className="bg-[#050505]/50 rounded-lg p-3 border border-[#8B1A1A]/10">
                    <p className="font-['Cormorant_Garamond'] text-lg font-bold" style={{ color: HAND_COLORS[result.npc_hand] || '#8B1A1A' }}>{result.npc_hand_display}</p>
                    {result.bluffed && <p className="font-['Outfit'] text-[#FFD700] text-[8px] mt-1">WAS BLUFFING</p>}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {!playing && !result && (
            <div className="text-center py-8">
              <p className="font-['Cormorant_Garamond'] text-xl text-[#9F7AEA]/15">TAKE A SEAT</p>
            </div>
          )}
        </div>
      </div>

      {/* Result */}
      {result && !playing && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg border text-center ${result.net > 0 ? 'bg-[#0a1a0a] border-[#4ADE80]/20' : result.net === 0 ? 'bg-[#111820] border-[#1A2535]' : 'bg-[#1a0505] border-[#8B1A1A]/20'}`}>
          <p className="font-['Cormorant_Garamond'] text-2xl font-bold" style={{ color: result.net > 0 ? '#4ADE80' : result.net === 0 ? '#6B7280' : '#8B1A1A' }}>
            {result.net > 0 ? `+$${result.net.toLocaleString()}` : result.net === 0 ? 'PUSH' : `-$${Math.abs(result.net).toLocaleString()}`}
          </p>
          <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-1">Balance: ${result.cash_after?.toLocaleString()}</p>
          <button onClick={() => setResult(null)} className="mt-3 px-4 py-1.5 bg-[#111820] border border-[#1A2535] rounded font-['Outfit'] text-[10px] text-[#E8E0D0]/40 hover:text-[#9F7AEA] tracking-widest transition-colors" data-testid="poker-again-btn">
            <RotateCcw size={12} className="inline mr-1" /> DEAL AGAIN
          </button>
        </motion.div>
      )}

      {!playing && !result && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 justify-center">
            {[5000, 25000, 100000, 500000, 1000000].map(v => (
              <button key={v} onClick={() => setBet(v)} className={`px-3 py-1.5 rounded font-['Outfit'] text-[10px] tracking-widest transition-all ${bet === v ? 'bg-[#9F7AEA]/20 border border-[#9F7AEA]/50 text-[#9F7AEA]' : 'bg-[#111820] border border-[#1A2535] text-[#E8E0D0]/30'}`}>
                ${v >= 1000 ? `${v/1000}K` : v}
              </button>
            ))}
          </div>
          <button onClick={play} className="w-full py-3 bg-gradient-to-r from-[#9F7AEA] to-[#7C3AED] text-[#050505] font-['Outfit'] text-sm tracking-widest rounded-lg hover:brightness-110 transition-all shadow-lg shadow-[#9F7AEA]/15" data-testid="play-poker-btn">
            DEAL — ${bet.toLocaleString()}
          </button>
        </div>
      )}
    </div>
  );
};


// ─── FIGHT BETTING — Red cage/octagon, fighter portraits, health bars ───
const FightGame = ({ gameId, venue, onClose, onResult }) => {
  const [bet, setBet] = useState(5000);
  const [fighter, setFighter] = useState(null);
  const [betType, setBetType] = useState('winner');
  const [fighting, setFighting] = useState(false);
  const [result, setResult] = useState(null);
  const [roundIdx, setRoundIdx] = useState(-1);
  const [fighters] = useState([
    { id: 'ironjaw', name: 'Iron Jaw', faction: 'BGM', power: 8, speed: 5, stamina: 9, record: '14-2' },
    { id: 'viper', name: 'La Vibora', faction: 'Los Diablos', power: 6, speed: 9, stamina: 7, record: '11-3' },
    { id: 'sledge', name: 'Sledge', faction: 'Independent', power: 10, speed: 3, stamina: 8, record: '18-1' },
    { id: 'ghost_hand', name: 'Ghost Hand', faction: 'PAW', power: 7, speed: 8, stamina: 6, record: '9-4' },
    { id: 'brick', name: 'Brick', faction: 'CMD', power: 9, speed: 4, stamina: 10, record: '16-0' },
    { id: 'razor', name: 'Razor', faction: 'DDN', power: 7, speed: 7, stamina: 7, record: '12-5' },
  ]);
  const BET_TYPES = [
    { id: 'winner', label: 'WINNER', payout: '2x' },
    { id: 'ko', label: 'KO FINISH', payout: '3.5x' },
    { id: 'underdog', label: 'UNDERDOG', payout: '4x' },
    { id: 'parlay', label: 'PARLAY', payout: '6x' },
  ];

  const play = async () => {
    if (!fighter) return;
    setFighting(true); setResult(null); setRoundIdx(-1);
    try {
      const res = await fetch(`${API}/api/gambling/${gameId}/fight`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue_id: venue.id, bet_amount: bet, fighter_id: fighter, bet_type: betType }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail); setFighting(false); return; }
      for (let i = 0; i < data.rounds.length; i++) {
        setRoundIdx(i); await new Promise(r => setTimeout(r, 600));
      }
      await new Promise(r => setTimeout(r, 500));
      setResult(data); setFighting(false); onResult(data);
    } catch { setFighting(false); }
  };

  const selectedFighter = fighters.find(f => f.id === fighter);

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#EF4444]">UNDERGROUND FIGHTS</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest">{venue.name}</p>
      </div>

      {/* Cage/Ring area */}
      <div className="relative rounded-2xl overflow-hidden min-h-[160px]"
        style={{ background: 'linear-gradient(180deg, #1a0505 0%, #0f0000 50%, #1a0505 100%)' }}>
        {/* Cage wire mesh overlay */}
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, #EF4444 0, #EF4444 1px, transparent 0, transparent 8px), repeating-linear-gradient(-45deg, #EF4444 0, #EF4444 1px, transparent 0, transparent 8px)',
        }} />
        {/* Red spotlight */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 20%, rgba(239,68,68,0.08), transparent 70%)' }} />

        <div className="relative z-10 p-4">
          {/* Fight animation — health bar style */}
          {fighting && result?.rounds && (
            <div className="space-y-2 py-2">
              <div className="flex items-center justify-between mb-3">
                <div className="text-center flex-1">
                  <p className="font-['Outfit'] text-xs text-[#C9A84C] font-bold">{selectedFighter?.name}</p>
                  <div className="h-2 bg-[#C9A84C]/10 rounded-full mt-1 overflow-hidden">
                    <motion.div className="h-full bg-[#C9A84C] rounded-full"
                      animate={{ width: `${Math.max(0, 100 - (roundIdx + 1) * 15)}%` }}
                      transition={{ duration: 0.3 }} />
                  </div>
                </div>
                <span className="font-['Outfit'] text-[#EF4444] text-xs mx-3 font-bold">VS</span>
                <div className="text-center flex-1">
                  <p className="font-['Outfit'] text-xs text-[#EF4444] font-bold">OPPONENT</p>
                  <div className="h-2 bg-[#EF4444]/10 rounded-full mt-1 overflow-hidden">
                    <motion.div className="h-full bg-[#EF4444] rounded-full"
                      animate={{ width: `${Math.max(0, 100 - (roundIdx + 1) * 12)}%` }}
                      transition={{ duration: 0.3 }} />
                  </div>
                </div>
              </div>
              {result.rounds.slice(0, roundIdx + 1).map((r, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                  className="bg-[#050505]/60 rounded px-3 py-1.5 flex items-center gap-2">
                  <span className="font-['Outfit'] text-[9px] text-[#EF4444]/60 w-8">RD {r.round}</span>
                  <span className="font-['Outfit'] text-[10px] text-[#E8E0D0]/40 flex-1">{r.pick_action} vs {r.opp_action}</span>
                  {r.ko && <span className="font-['Outfit'] text-[#EF4444] text-[10px] font-bold animate-pulse">KO!</span>}
                </motion.div>
              ))}
            </div>
          )}

          {/* Winner display */}
          {result && !fighting && (
            <div className="text-center py-4">
              <p className="font-['Outfit'] text-[9px] tracking-widest text-[#E8E0D0]/20 mb-2">WINNER</p>
              <p className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#EF4444]">{result.winner?.name}</p>
              <p className="font-['Outfit'] text-[10px] text-[#E8E0D0]/30 mt-1">{result.is_ko ? `KO Round ${result.ko_round}` : `Decision (${result.total_rounds} rounds)`}</p>
            </div>
          )}

          {!fighting && !result && (
            <div className="text-center py-6">
              <p className="font-['Cormorant_Garamond'] text-xl text-[#EF4444]/15">ENTER THE CAGE</p>
            </div>
          )}
        </div>
      </div>

      {/* Fighter selection */}
      {!fighting && !result && (
        <>
          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">PICK YOUR FIGHTER</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {fighters.map(f => (
              <button key={f.id} onClick={() => setFighter(f.id)}
                className={`p-3 rounded-lg border text-left transition-all ${fighter === f.id ? 'bg-[#EF4444]/10 border-[#EF4444]/40 shadow-lg shadow-[#EF4444]/10' : 'bg-[#111820] border-[#1A2535] hover:border-[#EF4444]/20'}`}
                data-testid={`fighter-${f.id}`}>
                <p className="font-['Outfit'] text-sm text-[#E8E0D0] font-semibold">{f.name}</p>
                <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/30">{f.faction} — {f.record}</p>
                <div className="flex gap-2 mt-1">
                  <span className="text-[8px] text-[#EF4444]">PWR {f.power}</span>
                  <span className="text-[8px] text-[#4A7AB5]">SPD {f.speed}</span>
                  <span className="text-[8px] text-[#4ADE80]">STA {f.stamina}</span>
                </div>
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {BET_TYPES.map(bt => (
              <button key={bt.id} onClick={() => setBetType(bt.id)}
                className={`flex-1 p-2 rounded text-center transition-all ${betType === bt.id ? 'bg-[#EF4444]/10 border border-[#EF4444]/30' : 'bg-[#111820] border border-[#1A2535]'}`}>
                <p className="font-['Outfit'] text-[9px] tracking-widest" style={{ color: betType === bt.id ? '#EF4444' : '#E8E0D0' }}>{bt.label}</p>
                <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/20">{bt.payout}</p>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 justify-center">
            {[5000, 25000, 100000, 500000].map(v => (
              <button key={v} onClick={() => setBet(v)} className={`px-3 py-1.5 rounded font-['Outfit'] text-[10px] tracking-widest transition-all ${bet === v ? 'bg-[#EF4444]/20 border border-[#EF4444]/50 text-[#EF4444]' : 'bg-[#111820] border border-[#1A2535] text-[#E8E0D0]/30'}`}>
                ${v >= 1000 ? `${v/1000}K` : v}
              </button>
            ))}
          </div>
          <button onClick={play} disabled={!fighter}
            className="w-full py-3 bg-gradient-to-r from-[#EF4444] to-[#B91C1C] text-[#050505] font-['Outfit'] text-sm tracking-widest rounded-lg hover:brightness-110 transition-all disabled:opacity-30 shadow-lg shadow-[#EF4444]/15" data-testid="play-fight-btn">
            PLACE BET — ${bet.toLocaleString()}
          </button>
        </>
      )}

      {result && !fighting && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg border text-center ${result.net > 0 ? 'bg-[#0a1a0a] border-[#4ADE80]/20' : 'bg-[#1a0505] border-[#8B1A1A]/20'}`}>
          <p className="font-['Cormorant_Garamond'] text-2xl font-bold" style={{ color: result.net >= 0 ? '#4ADE80' : '#8B1A1A' }}>
            {result.net >= 0 ? `+$${result.net.toLocaleString()}` : `-$${Math.abs(result.net).toLocaleString()}`}
          </p>
          <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px]">Balance: ${result.cash_after?.toLocaleString()}</p>
          <button onClick={() => { setResult(null); setFighter(null); }} className="mt-3 px-4 py-1.5 bg-[#111820] border border-[#1A2535] rounded font-['Outfit'] text-[10px] text-[#E8E0D0]/40 hover:text-[#EF4444] tracking-widest transition-colors">
            <RotateCcw size={12} className="inline mr-1" /> NEW FIGHT
          </button>
        </motion.div>
      )}
    </div>
  );
};


// ─── HUSTLE ROULETTE — Spinning wheel with distinct segments ───
const RouletteGame = ({ gameId, venue, onClose, onResult }) => {
  const [bet, setBet] = useState(5000);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [spinAngle, setSpinAngle] = useState(0);

  const SEGMENTS = [
    { id: 'wipeout', label: 'WIPEOUT', color: '#1a0000', textColor: '#EF4444' },
    { id: 'half', label: 'HALF', color: '#3d0000', textColor: '#EF4444' },
    { id: 'push', label: 'PUSH', color: '#374151', textColor: '#9CA3AF' },
    { id: 'double', label: '2X', color: '#1e3a5f', textColor: '#4A7AB5' },
    { id: 'triple', label: '3X', color: '#1e3a8f', textColor: '#2563EB' },
    { id: 'five', label: '5X', color: '#3d2d0a', textColor: '#C9A84C' },
    { id: 'ten', label: '10X', color: '#3d3d0a', textColor: '#FFD700' },
    { id: 'twenty', label: '20X', color: '#3d2200', textColor: '#FF6B35' },
    { id: 'fifty', label: '50X', color: '#3d1100', textColor: '#FF4500' },
    { id: 'hundred', label: '100X', color: '#3d0014', textColor: '#DC143C' },
    { id: 'blood', label: 'BLOOD TAX', color: '#4a0000', textColor: '#8B0000' },
  ];

  const play = async () => {
    setSpinning(true); setResult(null);
    setSpinAngle(prev => prev + 1440 + Math.random() * 720);
    try {
      const res = await fetch(`${API}/api/gambling/${gameId}/roulette`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue_id: venue.id, bet_amount: bet }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail); setSpinning(false); return; }
      await new Promise(r => setTimeout(r, 3000));
      setResult(data); setSpinning(false); onResult(data);
    } catch { setSpinning(false); }
  };

  const seg = result ? SEGMENTS.find(s => s.id === result.result_segment) : null;
  const segCount = SEGMENTS.length;
  const segAngle = 360 / segCount;

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#FF6B35]">HUSTLE ROULETTE</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest">{venue.name}</p>
      </div>

      {/* Wheel with distinct segments */}
      <div className="flex justify-center py-4 relative">
        {/* Pointer at top */}
        <div className="absolute top-2 z-20 flex flex-col items-center">
          <div className="w-0 h-0 border-l-[10px] border-r-[10px] border-t-[16px] border-l-transparent border-r-transparent border-t-[#C9A84C] drop-shadow-lg" />
        </div>

        <motion.div
          className="w-52 h-52 rounded-full border-4 border-[#C9A84C]/30 relative overflow-hidden shadow-[0_0_40px_rgba(201,168,76,0.1)]"
          style={{
            background: `conic-gradient(${SEGMENTS.map((s, i) => `${s.color} ${i * segAngle}deg ${(i + 1) * segAngle}deg`).join(', ')})`,
          }}
          animate={{ rotate: spinAngle }}
          transition={{ duration: 3.5, ease: [0.32, 0.94, 0.60, 1] }}
        >
          {/* Segment labels around the wheel */}
          {SEGMENTS.map((s, i) => {
            const angle = i * segAngle + segAngle / 2;
            return (
              <div key={s.id} className="absolute" style={{
                top: '50%', left: '50%',
                transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(-70px)`,
              }}>
                <span className="font-['Outfit'] text-[7px] font-bold tracking-wider"
                  style={{ color: s.textColor, transform: `rotate(-${angle}deg)`, display: 'block', textAlign: 'center' }}>
                  {s.label}
                </span>
              </div>
            );
          })}

          {/* Center hub */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-[#050505] border-2 border-[#C9A84C]/40 flex items-center justify-center shadow-xl">
              <span className="font-['Cormorant_Garamond'] text-sm font-bold text-[#C9A84C]">
                {spinning ? '...' : seg ? seg.label : 'SPIN'}
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {result && !spinning && (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          className={`p-4 rounded-lg border text-center ${result.is_blood_tax ? 'bg-[#2a0000] border-[#8B0000]/40' : result.net > 0 ? 'bg-[#0a1a0a] border-[#4ADE80]/20' : 'bg-[#1a0505] border-[#8B1A1A]/20'}`}>
          <p className="font-['Outfit'] text-xs tracking-widest mb-1" style={{ color: seg?.textColor }}>{result.result_label}</p>
          {result.is_blood_tax && <p className="font-['Outfit'] text-[#8B0000] text-[10px]">DOUBLE LOSS</p>}
          <p className="font-['Cormorant_Garamond'] text-3xl font-bold" style={{ color: result.net >= 0 ? '#C9A84C' : '#8B1A1A' }}>
            {result.net >= 0 ? `+$${result.net.toLocaleString()}` : `-$${Math.abs(result.net).toLocaleString()}`}
          </p>
          <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-1">Balance: ${result.cash_after?.toLocaleString()}</p>
          <button onClick={() => setResult(null)} className="mt-3 px-4 py-1.5 bg-[#111820] border border-[#1A2535] rounded font-['Outfit'] text-[10px] text-[#E8E0D0]/40 hover:text-[#FF6B35] tracking-widest transition-colors">
            <RotateCcw size={12} className="inline mr-1" /> SPIN AGAIN
          </button>
        </motion.div>
      )}
      {!spinning && !result && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 justify-center">
            {[10000, 50000, 250000, 1000000, 5000000].map(v => (
              <button key={v} onClick={() => setBet(v)} className={`px-3 py-1.5 rounded font-['Outfit'] text-[10px] tracking-widest transition-all ${bet === v ? 'bg-[#FF6B35]/20 border border-[#FF6B35]/50 text-[#FF6B35]' : 'bg-[#111820] border border-[#1A2535] text-[#E8E0D0]/30'}`}>
                ${v >= 1000 ? `${v/1000}K` : v}
              </button>
            ))}
          </div>
          <button onClick={play} className="w-full py-3 bg-gradient-to-r from-[#FF6B35] to-[#EA580C] text-[#050505] font-['Outfit'] text-sm tracking-widest rounded-lg hover:brightness-110 transition-all shadow-lg shadow-[#FF6B35]/15" data-testid="play-roulette-btn">
            SPIN — ${bet.toLocaleString()}
          </button>
        </div>
      )}
    </div>
  );
};


// ─── CRYPTO FLOOR — Terminal/matrix style, green-on-black ticker ───
const CryptoFloor = ({ gameId, venue, onClose, onResult }) => {
  const [mode, setMode] = useState(venue.game === 'crypto_casino' ? 'slots' : 'trade');
  const [prices, setPrices] = useState([]);
  const [holdings, setHoldings] = useState({});
  const [tradeAmt, setTradeAmt] = useState(10000);
  const [selectedCoin, setSelectedCoin] = useState('btc');
  const [slotBet, setSlotBet] = useState(5000);
  const [slotResult, setSlotResult] = useState(null);
  const [slotSpinning, setSlotSpinning] = useState(false);
  const [tradeResult, setTradeResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/gambling/${gameId}/crypto/prices`).then(r => r.json()).then(d => {
      setPrices(d.prices || []); setHoldings(d.holdings || {});
    }).catch(() => {});
  }, [gameId, tradeResult]);

  const trade = async (action) => {
    setTradeResult(null);
    try {
      const res = await fetch(`${API}/api/gambling/${gameId}/crypto/trade`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue_id: venue.id, action, coin_id: selectedCoin, amount: tradeAmt }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail); return; }
      setTradeResult(data); setHoldings(data.new_holdings || {}); onResult(data);
    } catch (e) { console.error('Trade error', e); }
  };

  const spinSlots = async () => {
    setSlotSpinning(true); setSlotResult(null);
    try {
      const res = await fetch(`${API}/api/gambling/${gameId}/crypto-slots`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue_id: venue.id, bet_amount: slotBet }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail); setSlotSpinning(false); return; }
      await new Promise(r => setTimeout(r, 2000));
      setSlotResult(data); setSlotSpinning(false); onResult(data);
    } catch { setSlotSpinning(false); }
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#4ADE80]">CRYPTO FLOOR</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest">{venue.name}</p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-1 bg-[#050505] rounded-lg p-1 border border-[#4ADE80]/10">
        {[{ id: 'trade', label: 'TRADING' }, { id: 'slots', label: 'CRYPTO SLOTS' }].map(m => (
          <button key={m.id} onClick={() => setMode(m.id)}
            className={`flex-1 py-2 rounded font-['Outfit'] text-[10px] tracking-widest transition-all ${mode === m.id ? 'bg-[#4ADE80]/10 text-[#4ADE80] border border-[#4ADE80]/20' : 'text-[#E8E0D0]/30'}`}>
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'trade' && (
        <div className="space-y-3">
          {/* Terminal-style market display */}
          <div className="rounded-xl border overflow-hidden"
            style={{ background: '#020804', borderColor: '#4ADE8015' }}>
            {/* Terminal header */}
            <div className="px-3 py-1.5 border-b flex items-center gap-2" style={{ borderColor: '#4ADE8010', background: '#4ADE8005' }}>
              <div className="w-1.5 h-1.5 rounded-full bg-[#4ADE80]" />
              <span className="font-mono text-[8px] text-[#4ADE80]/50">MARKET DATA — LIVE</span>
            </div>

            <div className="grid grid-cols-2 gap-px p-1" style={{ background: '#4ADE8008' }}>
              {prices.map(p => (
                <button key={p.id} onClick={() => setSelectedCoin(p.id)}
                  className={`p-3 text-left transition-all ${selectedCoin === p.id ? 'border-l-2' : 'border-l-2 border-l-transparent'}`}
                  style={{
                    background: selectedCoin === p.id ? '#4ADE8008' : '#020804',
                    borderLeftColor: selectedCoin === p.id ? p.color : 'transparent',
                  }}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-bold" style={{ color: p.color }}>{p.symbol}</span>
                    {p.trend === 'up' ? <TrendingUp size={10} className="text-[#4ADE80]" /> : p.trend === 'down' ? <TrendingDown size={10} className="text-[#EF4444]" /> : null}
                  </div>
                  <p className="font-mono text-[#E8E0D0] text-sm">${p.price.toLocaleString()}</p>
                  <p className="font-mono text-[9px]" style={{ color: p.change_pct >= 0 ? '#4ADE80' : '#EF4444' }}>{p.change_pct >= 0 ? '+' : ''}{p.change_pct}%</p>
                  {holdings[p.id] > 0 && <p className="font-mono text-[8px] text-[#C9A84C] mt-1">{holdings[p.id]?.toFixed(4)}</p>}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 justify-center">
            {[10000, 100000, 500000, 1000000, 5000000].map(v => (
              <button key={v} onClick={() => setTradeAmt(v)} className={`px-3 py-1.5 rounded font-mono text-[10px] tracking-widest transition-all ${tradeAmt === v ? 'bg-[#4ADE80]/15 border border-[#4ADE80]/40 text-[#4ADE80]' : 'bg-[#050505] border border-[#4ADE80]/10 text-[#E8E0D0]/30'}`}>
                ${v >= 1000 ? `${v/1000}K` : v}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => trade('buy')} className="py-3 bg-[#4ADE80] text-[#050505] font-mono text-sm tracking-widest rounded-lg hover:brightness-110" data-testid="crypto-buy-btn">
              BUY ${tradeAmt.toLocaleString()}
            </button>
            <button onClick={() => trade('sell')} className="py-3 bg-[#EF4444] text-[#050505] font-mono text-sm tracking-widest rounded-lg hover:brightness-110" data-testid="crypto-sell-btn">
              SELL
            </button>
          </div>
          {tradeResult && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="p-3 rounded-lg border text-center" style={{ background: '#020804', borderColor: '#4ADE8015' }}>
              <p className="font-mono text-xs text-[#4ADE80]">
                {tradeResult.action === 'buy' ? `Bought ${tradeResult.units?.toFixed(4)} ${tradeResult.coin?.symbol}` : `Sold ${tradeResult.units?.toFixed(4)} ${tradeResult.coin?.symbol} for $${tradeResult.revenue?.toLocaleString()}`}
              </p>
              <p className="font-mono text-[#E8E0D0]/30 text-[10px]">Balance: ${tradeResult.cash_after?.toLocaleString()}</p>
            </motion.div>
          )}
        </div>
      )}

      {mode === 'slots' && (
        <div className="space-y-4">
          <div className="flex justify-center gap-3 py-6">
            {[0, 1, 2].map(i => (
              <motion.div key={i}
                className="w-20 h-24 rounded-xl flex items-center justify-center border"
                style={{ background: '#020804', borderColor: '#4ADE8020' }}
                animate={slotSpinning ? { y: [0, -20, 20, -10, 10, 0] } : {}}
                transition={slotSpinning ? { duration: 0.3, repeat: Infinity, delay: i * 0.1 } : {}}>
                {slotResult ? (
                  <motion.span initial={{ scale: 2, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: i * 0.3 }}
                    className="text-3xl font-bold" style={{ color: slotResult.reels[i]?.color }}>
                    {slotResult.reels[i]?.label}
                  </motion.span>
                ) : <span className="text-2xl text-[#4ADE80]/10">?</span>}
              </motion.div>
            ))}
          </div>
          {slotResult && !slotSpinning && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-lg border text-center ${slotResult.net > 0 ? 'bg-[#020f04] border-[#4ADE80]/20' : 'bg-[#0f0204] border-[#8B1A1A]/20'}`}>
              {slotResult.is_triple && <p className="font-mono text-[#FFD700] text-xs tracking-widest mb-1">TRIPLE!</p>}
              {slotResult.is_skull_death && <p className="font-mono text-[#8B0000] text-xs tracking-widest mb-1">SKULL DEATH</p>}
              <p className="font-['Cormorant_Garamond'] text-2xl font-bold" style={{ color: slotResult.net >= 0 ? '#4ADE80' : '#8B1A1A' }}>
                {slotResult.net >= 0 ? `+$${slotResult.net.toLocaleString()}` : `-$${Math.abs(slotResult.net).toLocaleString()}`}
              </p>
              <p className="font-mono text-[#E8E0D0]/30 text-[10px]">Balance: ${slotResult.cash_after?.toLocaleString()}</p>
              <button onClick={() => setSlotResult(null)} className="mt-3 px-4 py-1.5 bg-[#050505] border border-[#4ADE80]/15 rounded font-mono text-[10px] text-[#4ADE80]/60 hover:text-[#4ADE80] tracking-widest transition-colors">
                <RotateCcw size={12} className="inline mr-1" /> SPIN AGAIN
              </button>
            </motion.div>
          )}
          {!slotSpinning && !slotResult && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 justify-center">
                {[5000, 25000, 100000, 500000, 1000000].map(v => (
                  <button key={v} onClick={() => setSlotBet(v)} className={`px-3 py-1.5 rounded font-mono text-[10px] tracking-widest transition-all ${slotBet === v ? 'bg-[#4ADE80]/15 border border-[#4ADE80]/40 text-[#4ADE80]' : 'bg-[#050505] border border-[#4ADE80]/10 text-[#E8E0D0]/30'}`}>
                    ${v >= 1000 ? `${v/1000}K` : v}
                  </button>
                ))}
              </div>
              <button onClick={spinSlots} className="w-full py-3 bg-[#4ADE80] text-[#050505] font-mono text-sm tracking-widest rounded-lg hover:brightness-110 transition-all" data-testid="play-slots-btn">
                SPIN — ${slotBet.toLocaleString()}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ─── Main Gambling Tab ───
const GamblingTab = ({ gameId, onRefresh }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeVenue, setActiveVenue] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/gambling/${gameId}/status`);
      if (res.ok) setStatus(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [gameId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleResult = () => { onRefresh(); };

  if (loading) return <div className="text-center py-12 text-[#E8E0D0]/20 font-['Outfit'] text-sm">Loading...</div>;
  if (!status) return <div className="text-center py-12 text-[#8B1A1A] font-['Outfit'] text-sm">Failed to load gambling</div>;

  if (!status.unlock.unlocked) {
    return (
      <div className="text-center py-16" data-testid="gambling-locked">
        <Lock size={48} className="text-[#C9A84C]/20 mx-auto mb-4" />
        <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#C9A84C]/40 mb-2">GAMBLING LOCKED</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-sm mb-4">Earn your way to the table.</p>
        <div className="flex justify-center gap-6">
          <div className="text-center">
            <p className="font-['Outfit'] text-[10px] tracking-widest text-[#E8E0D0]/20 mb-1">LEVEL</p>
            <p className={`font-['Outfit'] text-lg ${status.unlock.meets_level ? 'text-[#4ADE80]' : 'text-[#8B1A1A]'}`}>
              {status.unlock.level} / {status.unlock.level_required}
            </p>
          </div>
          <div className="text-center">
            <p className="font-['Outfit'] text-[10px] tracking-widest text-[#E8E0D0]/20 mb-1">MISSIONS</p>
            <p className={`font-['Outfit'] text-lg ${status.unlock.meets_missions ? 'text-[#4ADE80]' : 'text-[#8B1A1A]'}`}>
              {status.unlock.missions} / {status.unlock.missions_required}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (activeVenue) {
    const GAME_COMPONENTS = {
      card_pack: CardPackGame,
      street_dice: StreetDiceGame,
      slots: SlotsGame,
      blackjack: BlackjackGame,
      poker: PokerGame,
      fight_betting: FightGame,
      hustle_roulette: RouletteGame,
      crypto_trade: CryptoFloor,
      crypto_casino: CryptoFloor,
    };
    const GameComponent = GAME_COMPONENTS[activeVenue.game] || CardPackGame;
    const activeVenueImg = getGamblingImage(activeVenue);
    return (
      <div data-testid="gambling-active-game">
        <button onClick={() => setActiveVenue(null)}
          className="mb-4 font-['Outfit'] text-[#E8E0D0]/30 text-xs hover:text-[#C9A84C] transition-colors"
          data-testid="back-to-venues">
          &larr; BACK TO VENUES
        </button>
        {/* Venue hero banner */}
        <div className="relative rounded-xl overflow-hidden h-28 mb-4 pointer-events-none">
          <img src={activeVenueImg} alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e12] via-[#0a0e12]/50 to-transparent" />
          <div className="absolute bottom-3 left-4">
            <p className="font-['Outfit'] text-[8px] tracking-[0.3em] text-[#E8E0D0]/40">{activeVenue.type === 'backroom' ? 'BACK ROOM' : activeVenue.type === 'waterfront' ? 'WATERFRONT' : 'STREET'}</p>
            <h3 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0]">{activeVenue.name}</h3>
          </div>
        </div>
        <GameComponent gameId={gameId} venue={activeVenue} onClose={() => setActiveVenue(null)} onResult={handleResult} />
      </div>
    );
  }

  const venuesByType = {
    backroom: status.venues.filter(v => v.type === 'backroom'),
    street: status.venues.filter(v => v.type === 'street'),
    waterfront: status.venues.filter(v => v.type === 'waterfront'),
  };

  return (
    <div data-testid="gambling-venues">
      {status.stats && status.stats.games_played > 0 && (
        <div className="flex gap-3 mb-4 p-3 bg-[#0a0e12] rounded-lg overflow-x-auto">
          {[
            { label: 'PLAYED', val: status.stats.games_played, color: '#E8E0D0' },
            { label: 'WON', val: `$${(status.stats.total_won || 0).toLocaleString()}`, color: '#4ADE80' },
            { label: 'LOST', val: `$${(status.stats.total_lost || 0).toLocaleString()}`, color: '#EF4444' },
            { label: 'BIGGEST', val: `$${(status.stats.biggest_win || 0).toLocaleString()}`, color: '#FFD700' },
            { label: 'STREAK', val: status.stats.win_streak || 0, color: '#C9A84C' },
          ].map(s => (
            <div key={s.label} className="text-center shrink-0">
              <p className="font-['Outfit'] text-[8px] tracking-widest text-[#E8E0D0]/20">{s.label}</p>
              <p className="font-['Outfit'] text-sm" style={{ color: s.color + (s.label === 'PLAYED' ? '60' : '') }}>{s.val}</p>
            </div>
          ))}
        </div>
      )}

      {[
        { key: 'backroom', label: 'BACK ROOMS', sublabel: 'High risk. 2x payouts. No witnesses.', color: '#8B1A1A', icon: <Flame size={14} /> },
        { key: 'street', label: 'STREET GAMES', sublabel: 'Corner dice. Card tables. Medium risk.', color: '#C9A84C', icon: <MapPin size={14} /> },
        { key: 'waterfront', label: 'WATERFRONT', sublabel: 'Safe harbor. Tourist money. Low risk.', color: '#4A7AB5', icon: <Trophy size={14} /> },
      ].map(cat => venuesByType[cat.key]?.length > 0 && (
        <div key={cat.key} className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span style={{ color: cat.color }}>{cat.icon}</span>
            <h4 className="font-['Outfit'] text-xs tracking-widest" style={{ color: cat.color }}>{cat.label}</h4>
            <span className="font-['Outfit'] text-[9px] text-[#E8E0D0]/20">{cat.sublabel}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {venuesByType[cat.key].map(v => {
              const venueImg = getGamblingImage(v);
              return (
              <button key={v.id} onClick={() => setActiveVenue(v)}
                className="relative overflow-hidden rounded-lg text-left transition-all group"
                style={{ border: `1px solid ${cat.color}30` }}
                data-testid={`venue-${v.id}`}>
                {/* Venue background image */}
                <div className="absolute inset-0 pointer-events-none">
                  <img src={venueImg} alt="" className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, rgba(10,14,18,0.82) 0%, rgba(10,14,18,0.92) 60%, rgba(10,14,18,0.96) 100%)` }} />
                </div>
                <div className="relative z-10 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <h5 className="font-['Outfit'] text-sm text-[#E8E0D0] group-hover:text-[#C9A84C] transition-colors">{v.name}</h5>
                    <span className="font-['Outfit'] text-[8px] tracking-widest px-2 py-0.5 rounded"
                      style={{ color: cat.color, background: cat.color + '15' }}>
                      {v.game === 'card_pack' ? 'CARDS' : v.game === 'street_dice' ? 'DICE' : v.game === 'slots' ? 'SLOTS' : v.game === 'blackjack' ? '21' : v.game === 'poker' ? 'POKER' : v.game === 'fight_betting' ? 'FIGHTS' : v.game === 'hustle_roulette' ? 'ROULETTE' : v.game === 'crypto_trade' ? 'CRYPTO' : v.game === 'crypto_casino' ? 'SLOTS' : v.game.toUpperCase()}
                    </span>
                  </div>
                  <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px]">{v.description}</p>
                  <p className="font-['Outfit'] text-[9px] mt-1 tracking-widest" style={{ color: cat.color + '80' }}>ERA {v.era}</p>
                </div>
              </button>
              );
            })}
          </div>
        </div>
      ))}

      {status.venues.length === 0 && (
        <div className="text-center py-12">
          <p className="font-['Outfit'] text-[#E8E0D0]/30 text-sm">No gambling venues available in this era yet.</p>
        </div>
      )}
    </div>
  );
};

export default GamblingTab;
